import type { StaticTileData } from "../../types/cars";

export interface MarketingTileContent {
    variant: NonNullable<StaticTileData["tileType"]>;
    title: string;
    subtitle: string;
    description: string;
    buttonText: string;
    ctaSource?: string;
    promotionId?: string;
    promotionName?: string;
    creativeSlot?: string;
    userIntent?: "trade" | "sell" | "finance";
}

function includesValue(value: string | undefined, pattern: string): boolean {
    return value?.toLowerCase().includes(pattern.toLowerCase()) ?? false;
}

export function resolveMarketingTileVariant(data: StaticTileData): NonNullable<StaticTileData["tileType"]> {
    if (data.tileType) {
        return data.tileType;
    }

    if (data.tileLink?.includes("/pre-qualify") || includesValue(data.highlightedText, "financing")) {
        return "financing";
    }

    if (data.tileLink?.includes("tel:") || includesValue(data.highlightedText, "questions")) {
        return "support";
    }

    if (includesValue(data.textBeforeHighlighted, "appraise") || includesValue(data.highlightedText, "trade")) {
        return "trade_appraisal";
    }

    if (data.tileLink?.includes("/sell-my-car")) {
        return "sell_my_car";
    }

    return "support";
}

export function getMarketingTileContent(data: StaticTileData): MarketingTileContent {
    const variant = resolveMarketingTileVariant(data);

    switch (variant) {
        case "trade_appraisal":
            return {
                variant,
                title: "Appraise your trade",
                subtitle: "Know your number",
                description: "See what your current vehicle is worth before you buy.",
                buttonText: "Appraise My Trade",
                ctaSource: "cars_tile_trade_appraisal",
                promotionId: "cars_tile_trade_appraisal",
                promotionName: "Inventory Trade Appraisal Tile",
                creativeSlot: "inventory_trade_appraisal_tile",
                userIntent: "trade"
            };
        case "sell_my_car":
            return {
                variant,
                title: "Sell us your car",
                subtitle: "Get an instant offer",
                description: "We will buy your vehicle even if you do not buy from us.",
                buttonText: "Get Your Offer",
                ctaSource: "cars_tile_sell_my_car",
                promotionId: "cars_tile_sell_my_car",
                promotionName: "Inventory Sell My Car Tile",
                creativeSlot: "inventory_sell_my_car_tile",
                userIntent: "sell"
            };
        case "financing":
            return {
                variant,
                title: "Need financing?",
                subtitle: "Explore your financing options",
                description: "Get pre-qualified today.",
                buttonText: "Get Pre-Qualified",
                promotionId: "cars_tile_financing",
                promotionName: "Inventory Financing Tile",
                creativeSlot: "inventory_financing_tile",
                userIntent: "finance"
            };
        case "support":
        default:
            return {
                variant: "support",
                title: "Got questions?",
                subtitle: "We're here to help",
                description: "Call us anytime for support.",
                buttonText: "Call Now"
            };
    }
}
